import type { CoActionListItem } from './coActions';
import type { CompletionReportListItem } from './completionReports';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { Plan } from './plans';
import type { PostMortemListItem } from './postMortems';

export type DashboardSummary = {
  plans: {
    active: Plan[];
    doneLast7Days: Plan[];
    inProgressTotal: number;
    draftPendingTotal: number;
  };
  completionReports: {
    recent: CompletionReportListItem[];
    failedTotal: number;
    partialTotal: number;
  };
  postMortems: {
    recent: PostMortemListItem[];
    openTotal: number;
    inProgressTotal: number;
  };
  coActions: {
    recent: CoActionListItem[];
    openTotal: number;
  };
};

type DashboardSummaryResponse = {
  data: DashboardSummary;
};

export type AgentWorkloadItem = {
  agentConfigId: string;
  agentName: string;
  inProgressCount: number;
  completedLast7Days: number;
  avgQualityScore: number | null;
};

export type TrendDay = {
  date: string;
  completedPlans: number;
  avgQualityScore: number | null;
};

export type DashboardSummaryV2 = DashboardSummary & {
  agentWorkload: AgentWorkloadItem[];
  trends: TrendDay[];
  activeAgentCount: number;
};

type DashboardSummaryV2Response = {
  data: DashboardSummaryV2;
};

const DASHBOARD_SUMMARY_V2_QUERY = `
  query DashboardSummaryV2($projectId: ID!) {
    dashboardSummaryV2(projectId: $projectId)
  }
`;

type DashboardSummaryV2GraphqlPayload = {
  data?: { dashboardSummaryV2: DashboardSummaryV2 | null } | null;
  errors?: Array<{ message: string }>;
};

export const fetchDashboardSummaryV2 = async (token: string, projectId: string): Promise<DashboardSummaryV2> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/dashboard/v2`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch dashboard v2 summary: ${response.status}`);
  }

  const body = (await response.json()) as DashboardSummaryV2Response;
  return body.data;
};

export const fetchDashboardSummaryV2Graphql = async (token: string, projectId: string): Promise<DashboardSummaryV2> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/graphql`, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      query: DASHBOARD_SUMMARY_V2_QUERY,
      variables: { projectId },
      operationName: 'DashboardSummaryV2',
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch dashboard v2 summary: ${response.status}`);
  }

  const payload = (await response.json()) as DashboardSummaryV2GraphqlPayload;
  if (payload.errors && payload.errors.length > 0) {
    throw new Error(payload.errors[0]?.message ?? 'GraphQL dashboard v2 summary failed');
  }
  const summary = payload.data?.dashboardSummaryV2;
  if (!summary) {
    throw new Error('GraphQL dashboard v2 summary returned no data');
  }
  return summary;
};

export const fetchDashboardSummary = async (token: string, projectId: string): Promise<DashboardSummary> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/dashboard`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch dashboard summary: ${response.status}`);
  }

  const body = (await response.json()) as DashboardSummaryResponse;
  return body.data;
};
